import { useImperativeHandle, useRef } from "react";

import {
  FieldDefinitionFieldType,
  FieldImperativeHandle,
} from "@/components/@types";
import { FieldDefinitionUrlType } from "@/components/@types";
import { Skeleton } from "@/components/ui/skeleton";
import { typographyStyles } from "@/components/ui/typography";

function FieldUrlValue({
  value,
  style,
  ref,
}: FieldDefinitionFieldType<FieldDefinitionUrlType<any>> & {
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const linkRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        linkRef.current?.focus();
      },
      unsetFocus: () => {
        linkRef.current?.blur();
      },
    }),
    [],
  );

  const { url, label } = value;

  const handleClick = () => {
    if (value) {
      window.open(url, "_blank");
    }
  };

  return (
    <div
      ref={linkRef}
      tabIndex={0}
      role="link"
      data-testid={`field-url-${label}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      style={
        {
          display: "flex",
          alignItems: "center",
          padding: `${"0px"} ${"8px"}`,
          ...(typographyStyles["body-md"] as React.CSSProperties),
          color: "var(--primary)",
          cursor: "pointer",
          textDecoration: "underline",
          outline: "none",
          ...style,
        } as React.CSSProperties
      }
      onClick={handleClick}
    >
      {label}
    </div>
  );
}

function FieldUrlSkeleton() {
  return (
    <Skeleton
      variant="text"
      level="body-md"
      style={{
        width: "100%",
        minWidth: 0,
        paddingLeft: "8px",
        paddingRight: "8px",
      }}
    />
  );
}

export function FieldUrl({
  isLoading,
  ref,
  ...props
}: FieldDefinitionFieldType<FieldDefinitionUrlType<any>> & {
  isLoading?: boolean;
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  if (isLoading) {
    return <FieldUrlSkeleton />;
  }

  return <FieldUrlValue {...props} ref={ref} />;
}
